import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { checkAndSetStats, isMobileUserAgent, encodeArticleForUrl } from "../../helpers/helpers";
import Footer from "../../components/Footer";
import Navigation from "../../components/Navigation";
import SEO from "../../components/SEO";
import { Sidebar } from "primereact/sidebar";
import ContactMeBlock from "../../components/ContactMeBlock";
import { useNavigate } from "react-router-dom";
import { useBasket } from "../../context/BasketContext";
import { database, ref, get } from "../../firebase";

// Custom scroll animation hook with threshold
const useLuxuryScrollAnimation = (threshold = 0.15) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const observerRef = useRef(null);

    useEffect(() => {
        const element = ref.current;

        // Skip if already visible or no element
        if (isVisible || !element) return;

        // Skip if already observing this element
        if (observerRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                    observerRef.current = null;
                }
            },
            { threshold, rootMargin: "0px 0px -100px 0px" }
        );

        observer.observe(element);
        observerRef.current = observer;

        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    });

    return [ref, isVisible];
};

// Parallax scroll hook
const useParallax = (speed = 0.5) => {
    const ref = useRef(null);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                const scrolled = window.innerHeight - rect.top;
                setOffset(scrolled * speed);
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [speed]);

    return [ref, offset];
};

// Luxury Product Card - Completely custom design
const LuxuryProductCard = ({ item, index, isVisible, buttonTexts }) => {
    const navigate = useNavigate();
    const { addToBasket, isInBasket } = useBasket();
    const [isAdded, setIsAdded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const inBasket = isInBasket(item?.article);

    const handleProductClick = () => {
        const encodedArticle = encodeArticleForUrl(item.article);
        // Always use 'luxury' collection for products on this page
        navigate(`/catalog/luxury/${encodedArticle}`);
    };

    const handleAddToBasket = (e) => {
        e.stopPropagation();
        if (inBasket) {
            navigate("/basket");
        } else {
            addToBasket(item);
            setIsAdded(true);
            setTimeout(() => setIsAdded(false), 2000);
        }
    };

    return (
        <div
            className={`luxury-scroll-rotate ${isVisible ? 'is-visible' : ''} group cursor-pointer`}
            style={{ transitionDelay: `${index * 150}ms` }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleProductClick}
        >
            {/* Card Container */}
            <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] rounded-2xl overflow-hidden luxury-hover-shine luxury-border-glow">
                {/* Decorative corners */}
                <div className="luxury-corner-decoration top-left" />
                <div className="luxury-corner-decoration top-right" />

                {/* Image Container */}
                <div className="relative aspect-square overflow-hidden">
                    {/* Animated background gradient */}
                    <div
                        className={`absolute inset-0 bg-gradient-to-br from-brand-gold/20 via-transparent to-brand-gold/10 transition-opacity duration-700 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                    />

                    {/* Floating ring glow effect */}
                    <div
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ${isHovered ? 'scale-110' : 'scale-100'}`}
                    >
                        <div className="absolute w-48 h-48 rounded-full bg-brand-gold/5 blur-3xl luxury-pulse-ring" />
                    </div>

                    {/* Product Image */}
                    <img
                        src={item.image}
                        alt={item.imageAlt}
                        className={`w-full h-full object-cover transition-all duration-1000 ${isHovered ? 'scale-110 brightness-110' : 'scale-100'}`}
                    />

                    {/* Shimmer overlay on hover */}
                    <div
                        className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ${isHovered ? 'translate-x-full' : '-translate-x-full'}`}
                    />

                    {/* Price badge hidden */}
                </div>

                {/* Content */}
                <div className="p-6 lg:p-8">
                    {/* Article */}
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-[1px] bg-gradient-to-r from-brand-gold to-transparent" />
                        <span className="text-brand-gold text-sm tracking-[0.3em] uppercase">{item.article}</span>
                    </div>

                    {/* Description */}
                    <p className="text-[#9a9a9a] text-sm lg:text-base leading-relaxed mb-6 line-clamp-2">
                        {item.description}
                    </p>

                    {/* Price Block hidden */}

                    {/* Add to basket button */}
                    <button
                        onClick={handleAddToBasket}
                        disabled={isAdded}
                        className={`w-full py-4 rounded-xl font-medium tracking-wide uppercase text-sm transition-all duration-500
                            ${isAdded
                                ? 'bg-green-500/20 border border-green-500 text-green-400'
                                : inBasket
                                    ? 'bg-transparent border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-black'
                                    : 'bg-brand-gold text-black hover:bg-brand-gold-hover hover:shadow-[0_0_30px_rgba(201,169,98,0.4)]'
                            }`}
                    >
                        <span className="flex items-center justify-center gap-2">
                            {isAdded ? (
                                <>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {buttonTexts?.addedToCart}
                                </>
                            ) : inBasket ? (
                                <>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    {buttonTexts?.inCart}
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    {buttonTexts?.addToCart}
                                </>
                            )}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// Luxury Section Header
const LuxurySectionHeader = ({ subtitle, title, description, isVisible }) => {
    return (
        <div className="text-center mb-16 lg:mb-24">
            <div className={`luxury-scroll-reveal ${isVisible ? 'is-visible' : ''}`}>
                <span className="inline-block text-brand-gold text-xs lg:text-sm font-medium tracking-[0.4em] uppercase mb-6">
                    {subtitle}
                </span>
            </div>
            <div className={`luxury-scroll-reveal ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                <h2 className="text-4xl lg:text-6xl xl:text-7xl font-extralight text-white tracking-tight mb-6">
                    {title}
                </h2>
            </div>
            {description && (
                <div className={`luxury-scroll-reveal ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '200ms' }}>
                    <p className="text-[#777] text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed">
                        {description}
                    </p>
                </div>
            )}
            <div className={`luxury-scroll-reveal ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '300ms' }}>
                <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-brand-gold to-transparent mx-auto mt-10" />
            </div>
        </div>
    );
};

// Luxury Product Section
const LuxuryProductSection = ({ subtitle, title, description, products, bgVariant = "dark", buttonTexts }) => {
    const [ref, isVisible] = useLuxuryScrollAnimation(0.1);

    const bgClass = bgVariant === "dark"
        ? "bg-[#0a0a0a]"
        : "bg-gradient-to-b from-[#0a0a0a] via-[#111] to-[#0a0a0a]";

    return (
        <section ref={ref} className={`py-24 lg:py-40 px-6 lg:px-16 xl:px-24 ${bgClass} relative overflow-hidden`}>
            {/* Background decorations */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-gold/3 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-brand-gold/5 rounded-full blur-[120px]" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                <LuxurySectionHeader
                    subtitle={subtitle}
                    title={title}
                    description={description}
                    isVisible={isVisible}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12">
                    {products.map((product, index) => (
                        <LuxuryProductCard
                            key={product.article}
                            item={product}
                            index={index}
                            isVisible={isVisible}
                            buttonTexts={buttonTexts}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

// Loading Skeleton Component
const LuxuryLoadingSkeleton = () => (
    <div className="bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-center">
            <div className="w-16 h-16 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[#666] text-sm tracking-[0.3em] uppercase">Loading</p>
        </div>
    </div>
);

function Luxury() {
    const doesStatsSent = useRef(false);
    const [contactVisible, setContactVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [content, setContent] = useState(null);
    const isMobile = isMobileUserAgent();
    const diamondsRef = useRef(null);
    const [heroParallaxRef, heroOffset] = useParallax(0.3);
    const [introRef, introVisible] = useLuxuryScrollAnimation(0.2);
    const [ctaRef, ctaVisible] = useLuxuryScrollAnimation(0.2);

    useLayoutEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    useEffect(() => {
        document.body.style.backgroundColor = "#050505";
        return () => {
            document.body.style.backgroundColor = "";
        };
    }, []);

    useEffect(() => {
        if (!doesStatsSent.current) {
            checkAndSetStats("luxuryPage");
            doesStatsSent.current = true;
        }
    }, []);

    // Fetch luxury page content from Firebase
    useEffect(() => {
        const dbRef = ref(database, "/luxury");

        get(dbRef)
            .then((snapshot) => {
                if (snapshot.exists()) {
                    setContent(snapshot.val());
                } else {
                    console.log("No data available for /luxury path");
                }
            })
            .catch((error) => {
                console.error("Error fetching luxury data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const scrollToDiamonds = () => {
        diamondsRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    if (isLoading || !content) {
        return <LuxuryLoadingSkeleton />;
    }

    return (
        <div className="bg-[#050505] min-h-screen overflow-x-hidden">
            <SEO
                title={content.titles.seo?.title || "Luxury Jewelry"}
                description={content.titles.seo?.description || ""}
                url="/luxury"
            />
            <Navigation showBack={true} backTo="/" variant="dark" sticky={true} />

            <main role="main">
                {/* HERO SECTION - Apple-inspired dramatic entrance */}
                <header
                    ref={heroParallaxRef}
                    className="relative min-h-screen flex items-center justify-center overflow-hidden"
                >
                    {/* Animated background layers */}
                    <div className="absolute inset-0">
                        {/* Deep space gradient */}
                        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0808] to-[#050505]" />

                        {/* Animated glow orbs */}
                        <div
                            className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full luxury-glow"
                            style={{
                                background: 'radial-gradient(circle, rgba(201,169,98,0.15) 0%, transparent 70%)',
                                transform: `translate(-50%, ${heroOffset * 0.5}px)`
                            }}
                        />
                        <div
                            className="absolute top-1/2 left-1/4 w-[400px] h-[400px] rounded-full luxury-float"
                            style={{ background: 'radial-gradient(circle, rgba(201,169,98,0.1) 0%, transparent 70%)' }}
                        />
                        <div
                            className="absolute top-1/3 right-1/4 w-[300px] h-[300px] rounded-full luxury-float"
                            style={{
                                background: 'radial-gradient(circle, rgba(201,169,98,0.08) 0%, transparent 70%)',
                                animationDelay: '2s'
                            }}
                        />

                        {/* Rotating decorative ring */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] lg:w-[900px] lg:h-[900px] luxury-rotate-slow opacity-20">
                            <div className="absolute inset-0 border border-brand-gold/30 rounded-full" />
                            <div className="absolute inset-8 border border-brand-gold/20 rounded-full" />
                            <div className="absolute inset-16 border border-brand-gold/10 rounded-full" />
                        </div>

                        {/* Sparkle particles */}
                        {[...Array(20)].map((_, i) => (
                            <div
                                key={i}
                                className="absolute w-1 h-1 bg-brand-gold rounded-full"
                                style={{
                                    top: `${Math.random() * 100}%`,
                                    left: `${Math.random() * 100}%`,
                                    opacity: Math.random() * 0.5 + 0.2,
                                    animation: `luxuryGlow ${3 + Math.random() * 4}s ease-in-out infinite`,
                                    animationDelay: `${Math.random() * 3}s`
                                }}
                            />
                        ))}
                    </div>

                    {/* Hero Content */}
                    <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
                        {/* Decorative line above */}
                        <div className="luxury-line-expand luxury-delay-300 w-20 h-[1px] bg-brand-gold/50 mx-auto mb-12" />

                        {/* Main Title */}
                        <h1 className="luxury-reveal">
                            <span className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-extralight text-white tracking-tight">
                                {content.titles.hero?.titleLine1}
                            </span>
                        </h1>
                        <h1 className="luxury-reveal luxury-delay-200">
                            <span className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-extralight luxury-gradient-text tracking-tight mt-2">
                                {content.titles.hero?.titleLine2}
                            </span>
                        </h1>

                        {/* Subtitle */}
                        <p className="luxury-reveal luxury-delay-400 text-[#888] text-lg lg:text-2xl font-light leading-relaxed max-w-2xl mx-auto mt-10">
                            {content.titles.hero?.subtitle}
                        </p>

                        {/* CTA Button */}
                        <div className="luxury-reveal luxury-delay-600 mt-14">
                            <button
                                onClick={scrollToDiamonds}
                                className="group relative inline-flex items-center gap-4 bg-transparent border border-brand-gold/50 text-white px-12 py-5 rounded-full text-lg font-light tracking-wide transition-all duration-700 hover:bg-brand-gold hover:text-black hover:border-brand-gold hover:shadow-[0_0_60px_rgba(201,169,98,0.4)]"
                            >
                                <span>{content.titles.hero?.ctaButton}</span>
                                <svg className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </button>
                        </div>

                        {/* Decorative line below */}
                        <div className="luxury-line-expand luxury-delay-800 w-20 h-[1px] bg-brand-gold/50 mx-auto mt-16" />
                    </div>

                    {/* Scroll indicator */}
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 luxury-reveal luxury-delay-1000">
                        <div className="flex flex-col items-center gap-3">
                            <span className="text-[#555] text-xs tracking-[0.3em] uppercase">{content.titles.hero?.scrollIndicator}</span>
                            <div className="w-[1px] h-12 bg-gradient-to-b from-brand-gold/50 to-transparent relative overflow-hidden">
                                <div className="absolute top-0 w-full h-4 bg-brand-gold animate-bounce" />
                            </div>
                        </div>
                    </div>
                </header>

                {/* INTRO SECTION - Luxury philosophy */}
                <section
                    ref={introRef}
                    className="py-32 lg:py-48 px-6 lg:px-16 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505] relative overflow-hidden"
                >
                    {/* Background gradient */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-brand-gold/3 rounded-full blur-[200px]" />
                    </div>

                    <div className="max-w-6xl mx-auto relative z-10">
                        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                            {/* Left - Text */}
                            <div>
                                <div className={`luxury-scroll-reveal ${introVisible ? 'is-visible' : ''}`}>
                                    <span className="text-brand-gold text-xs tracking-[0.4em] uppercase">{content.titles.intro?.label}</span>
                                </div>
                                <div className={`luxury-scroll-reveal ${introVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                                    <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extralight text-white mt-6 leading-tight">
                                        {content.titles.intro?.titleLine1}<br />
                                        <span className="luxury-gradient-text">{content.titles.intro?.titleLine2}</span>
                                    </h2>
                                </div>
                                <div className={`luxury-scroll-reveal ${introVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '200ms' }}>
                                    <p className="text-[#777] text-lg lg:text-xl leading-relaxed mt-8">
                                        {content.titles.intro?.paragraph1}
                                    </p>
                                </div>
                                <div className={`luxury-scroll-reveal ${introVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '300ms' }}>
                                    <p className="text-[#555] text-base leading-relaxed mt-6">
                                        {content.titles.intro?.paragraph2}
                                    </p>
                                </div>
                            </div>

                            {/* Right - Feature Cards */}
                            <div className="space-y-6">
                                {(content.titles.intro?.features || []).map((item, index) => (
                                    <div
                                        key={index}
                                        className={`luxury-scroll-reveal ${introVisible ? 'is-visible' : ''} group`}
                                        style={{ transitionDelay: `${400 + index * 100}ms` }}
                                    >
                                        <div className="luxury-glass rounded-2xl p-8 transition-all duration-500 hover:bg-brand-gold/5 luxury-hover-shine">
                                            <div className="flex items-start gap-6">
                                                <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-gold/20 transition-colors duration-500">
                                                    <span className="text-brand-gold text-xl">{item.icon}</span>
                                                </div>
                                                <div>
                                                    <h3 className="text-white text-xl font-light mb-2">{item.title}</h3>
                                                    <p className="text-[#666] text-sm leading-relaxed">{item.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Decorative divider */}
                <div className="luxury-divider" />

                {/* DIAMONDS SECTION */}
                <div ref={diamondsRef}>
                    <LuxuryProductSection
                        subtitle={content.titles.sections?.diamonds?.subtitle}
                        title={content.titles.sections?.diamonds?.title}
                        description={content.titles.sections?.diamonds?.description}
                        products={content.products?.firstSection || []}
                        bgVariant="gradient"
                        buttonTexts={content.titles.productCard}
                    />
                </div>

                {/* WHITE GOLD SECTION */}
                <LuxuryProductSection
                    subtitle={content.titles.sections?.whiteGold?.subtitle}
                    title={content.titles.sections?.whiteGold?.title}
                    description={content.titles.sections?.whiteGold?.description}
                    products={content.products?.secondSection || []}
                    bgVariant="dark"
                    buttonTexts={content.titles.productCard}
                />

                {/* CLASSIC GOLD SECTION */}
                <LuxuryProductSection
                    subtitle={content.titles.sections?.classicGold?.subtitle}
                    title={content.titles.sections?.classicGold?.title}
                    description={content.titles.sections?.classicGold?.description}
                    products={content.products?.thirdSections || []}
                    bgVariant="gradient"
                    buttonTexts={content.titles.productCard}
                />

                {/* CTA SECTION */}
                <section
                    ref={ctaRef}
                    className="py-32 lg:py-48 px-6 relative overflow-hidden"
                >
                    {/* Background */}
                    <div className="absolute inset-0">
                        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#080808] to-[#050505]" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-gold/5 rounded-full blur-[200px]" />

                        {/* Decorative rings */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-brand-gold/10 rounded-full" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-brand-gold/5 rounded-full" />
                    </div>

                    <div className="max-w-4xl mx-auto text-center relative z-10">
                        <div className={`luxury-scroll-scale ${ctaVisible ? 'is-visible' : ''}`}>
                            <span className="text-brand-gold text-xs tracking-[0.4em] uppercase">{content.titles.cta?.label}</span>
                        </div>

                        <div className={`luxury-scroll-scale ${ctaVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                            <h2 className="text-4xl lg:text-6xl xl:text-7xl font-extralight text-white mt-8 leading-tight">
                                {content.titles.cta?.titleLine1}<br />
                                <span className="luxury-gradient-text">{content.titles.cta?.titleLine2}</span>
                            </h2>
                        </div>

                        <div className={`luxury-scroll-scale ${ctaVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '200ms' }}>
                            <p className="text-[#777] text-lg lg:text-xl max-w-2xl mx-auto mt-8 leading-relaxed">
                                {content.titles.cta?.description}
                            </p>
                        </div>

                        <div className={`luxury-scroll-scale ${ctaVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '300ms' }}>
                            <button
                                onClick={() => setContactVisible(true)}
                                className="mt-12 group relative inline-flex items-center gap-4 bg-brand-gold text-black px-14 py-6 rounded-full text-lg font-medium tracking-wide transition-all duration-700 hover:bg-brand-gold-hover hover:shadow-[0_0_80px_rgba(201,169,98,0.5)] hover:scale-105"
                            >
                                <span>{content.titles.cta?.button}</span>
                                <div className="w-10 h-10 rounded-full bg-black/10 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </div>
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            {/* Contact Sidebar */}
            <Sidebar
                className={isMobile ? "w-full" : "w-1/2"}
                visible={contactVisible}
                position="right"
                onHide={() => setContactVisible(false)}
            >
                <ContactMeBlock />
            </Sidebar>

            <Footer isDark={true} containerClassNames="bg-[#050505] text-[#888]" />
        </div>
    );
}

export default Luxury;
